import { SignUpFormData, User } from "../domain/types/auth.types";

export class AuthService {
  // Mock implementation - replace with actual API calls
  static async signUp(
    data: SignUpFormData
  ): Promise<{ user: User; requiresVerification: boolean }> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Mock user creation
    const user: User = {
      id: Math.random().toString(36).substr(2, 9),
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      // phone: data.phone,
      mfaRequired: false,
      createdAt: new Date(),
    };

    // Store in localStorage for demo
    localStorage.setItem("pendingUser", JSON.stringify(user));

    return {
      user,
      requiresVerification: true,
    };
  }

  static async login(
    email: string,
    password: string
  ): Promise<{
    user: User | null;
    authToken: string | null;
    requiresMFA: boolean;
    preAuthToken?: string;
  }> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Mock login - check if user exists
    const storedUser = localStorage.getItem("currentUser");

    // For demo, accept any email/password and create/return a user
    const user: User = storedUser
      ? JSON.parse(storedUser)
      : {
          id: Math.random().toString(36).substr(2, 9),
          email: email,
          firstName: "John",
          lastName: "Doe",
          phone: "+1234567890",
          clinicName: "Demo Practice",
          mfaRequired: false,
          username: email,
          roles: ["user"],
          createdAt: new Date(),
        };

    // Store pending login user for MFA
    localStorage.setItem("pendingLoginUser", JSON.stringify(user));

    // Mock MFA requirement (always true for demo)
    return {
      user,
      authToken: null, // No auth token until MFA is completed
      requiresMFA: true,
    };
  }

  static async sendMFACode(method: "sms" | "email"): Promise<void> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    console.log(`Sending MFA code via ${method}...`);
    // In real app, this would trigger an API call to send the code
  }

  static async verifyMFA(
    method: "sms" | "email" | "authenticator",
    code: string
  ): Promise<boolean> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Mock verification - accept any 6-digit code for demo
    return code.length === 6;
  }

  static completeMFALogin(): User | null {
    const userStr = localStorage.getItem("pendingLoginUser");
    if (userStr) {
      const user = JSON.parse(userStr);
      localStorage.setItem("currentUser", JSON.stringify(user));
      localStorage.removeItem("pendingLoginUser");
      return user;
    }
    return null;
  }

  static async verifyEmail(email: string, code: string): Promise<boolean> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Mock verification - accept any 6-digit code for demo
    return code.length === 6 && /^\d+$/.test(code);
  }

  static async verifyPhone(phone: string, code: string): Promise<boolean> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Mock verification - accept any 6-digit code for demo
    return code.length === 6 && /^\d+$/.test(code);
  }

  static async resendCode(type: "email" | "phone"): Promise<void> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    console.log(`Resending ${type} verification code...`);
  }

  static async requestPasswordReset(email: string): Promise<void> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Store email for demo purposes
    localStorage.setItem("resetEmail", email);
    console.log(`Password reset code sent to ${email}`);
  }

  static async verifyResetCode(code: string): Promise<boolean> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Mock verification - accept any 6-digit code for demo
    const isValid = code.length === 6 && /^\d+$/.test(code);
    if (isValid) {
      localStorage.setItem("resetCodeVerified", "true");
    }
    return isValid;
  }

  static async resetPassword(newPassword: string): Promise<void> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Check if code was verified
    const isVerified = localStorage.getItem("resetCodeVerified") === "true";
    if (!isVerified) {
      throw new Error("Reset code not verified");
    }

    // Clear reset data
    localStorage.removeItem("resetEmail");
    localStorage.removeItem("resetCodeVerified");

    console.log("Password successfully reset");
  }

  static async resendResetCode(): Promise<void> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const email = localStorage.getItem("resetEmail");
    if (email) {
      console.log(`Resending reset code to ${email}`);
    }
  }

  static getCurrentUser(): User | null {
    const userStr = localStorage.getItem("currentUser");
    return userStr ? JSON.parse(userStr) : null;
  }

  static saveCurrentUser(user: User): void {
    localStorage.setItem("currentUser", JSON.stringify(user));
    localStorage.removeItem("pendingUser");
  }

  static getAuthToken(): string | null {
    return localStorage.getItem("authToken");
  }

  static saveAuthToken(token: string): void {
    localStorage.setItem("authToken", token);
  }

  static clearAuthToken(): void {
    localStorage.removeItem("authToken");
  }
}
